import { INTERNAL_ROW_KEY, SCROLLBAR_POSITION, TABLE_MODE } from '../config'

const rowHeight = {
  type: Number,
  default: 32,
}

export const tableProps = {
  // 表格宽度
  width: {
    type: [Number, String],
    validator: function (val) {
      return val && isFinite(val)
    },
  },
  // 表格高度
  height: {
    type: [Number, String],
    validator: function (val) {
      return val && isFinite(val)
    },
  },
  // 表格最大高度
  maxHeight: String | Number,
  data: {
    type: Array,
    default: () => [],
  },
  // 列 column 的配置数组
  columns: {
    type: Array,
    default: () => [],
  },
  border: {
    type: Boolean,
    default: true,
  },
  // 显示表头
  fixedHeader: Boolean,
  rowClass: String, // row类名
  // 每行的高度, 用于计算表的总高度
  rowHeight,
  // 每行的 key 值，如果不提供，将使用索引 index 代替
  rowKey: {
    type: String | Function,
    default: INTERNAL_ROW_KEY,
  },
  defaultExpandAll: Boolean,
  className: String | Array | Object, // 表格的类名称，将应用于表格的全部的三个部分 (左、右、主)
  // 水平滚动条大小
  hScrollbarSize: {
    type: Number,
    default: 10,
  },
  // 垂直滚动条大小
  vScrollbarSize: {
    type: Number,
    default: 10,
  },
  // 如果开启，滚动条将一直显示，反之只会在鼠标经过时显示
  scrollbarAlwaysOn: {
    type: Boolean,
    default: true,
  },
  // 滚动条位置 inside ｜ outside
  scrollbarPosition: {
    type: String,
    default: SCROLLBAR_POSITION[0],
  },
  // 空数据时显示的文本内容
  emptyText: String,
  // 展示树形数据时，树节点的缩进
  indent: {
    type: Number,
    default: 16,
  },
  lazy: Boolean, // 是否懒加载子节点数据
  load: Function, // 加载子节点数据的函数，lazy 为 true 时生效
  showHeader: Boolean, // 是否显示表头
  scroll: Function, // 滚动时触发
  // 估计行高，用于优化滚动性能
  estimatedRowHeight: rowHeight,
  // footer 部分的高度
  footerHeight: {
    type: Number,
    default: 0,
  },
  headerHeight: {
    type: Number,
    default: 32,
  },
  headerAlign: String,
  defaultSort: Object,
  showIndexType: {
    type: String,
    default: 'fixd', // 这里只是注释默认是按内容，传入为fixd showIndex序号为固定有序
  },
  fixedRowsTop: Number,
  fixedRowsData: Array,
  cacheSize: {
    type: Number,
    default: 2,
  },
  showIndex: {
    type: Boolean,
    default: false,
  },
  showSelection: {
    type: Boolean,
    default: false,
  },
  showRadio: {
    type: Boolean,
    default: false,
  },
  radioProps: {
    type: Object,
    default: () => ({}),
  },
  // 展开相关
  treeProps: {
    type: Object,
    default: () => ({
      children: 'children',
      hasChildren: 'hasChildren',
    }),
  },
  // 展开表格的key集合
  expandedRowKeys: {
    type: Array,
    default: () => [],
  },
  // 是否开启展开行
  enableExpandRow: {
    type: Boolean,
    default: false,
  },
  selectable: Function,
  reserveSelection: Boolean, // 是否保留历史的勾选，比如数据替换之后
  // 默认缩进的值
  indentSize: {
    type: Number,
    default: 12,
  },
  // 展开列的key名称
  expandColumnKey: String,
  // 展开的icon大小
  iconSize: {
    type: Number,
    default: 12,
  },
  spanMethod: Function,
  indexMethod: Function,
  headerRowStyle: [Function, Object],
  headerRowClassName: [Function, String],
  headerCellClassName: [Function, String],
  headerCellStyle: [Function, Object],
  cellStyle: [Function, Object],
  cellClassName: [Function, String],
  footerRows: Array,
  showOverflowTooltip: {
    type: Boolean,
    default: true,
  },
  showOverflowTooltipOnHeader: {
    type: Boolean,
    default: false,
  },
  touchPopoverContent: {
    // 触摸气泡内容
    type: Boolean,
    default: false,
  },
  highlightCurrentRow: {
    type: Boolean,
    default: false,
  },
  // 数据变化是否重置到顶部
  autoResetScrollTop: {
    type: Boolean,
    default: true,
  },
  tableName: String,
  mergeCellCacheSize: {
    type: Number,
    default: 3,
  },
  enableDynamicHeight: {
    type: Boolean,
    default: false,
  },
  noWrap: {
    type: Boolean,
    default: false,
  },
  scrollbarStyle: Object,
  // 预留高度，用于动态设置表格最终高度
  reserveHeight: {
    type: Number,
    default: 12,
  },
  loading: Boolean,
  loadingText: String,
  loadingSpinner: String,
  loadingBackground: String,
  loadingCustomClass: String,
  showEmpty: {
    type: Boolean,
    default: false,
  },
  rowCellCanSelected: {
    type: Boolean,
    default: false,
  },
  mergeEqualCellsOption: Object, // 相同单元格数据可合并的配置选项。
  mergeCells: Array, // 合并行列的配置
  mergeCellsScope: {
    type: Object,
    default: () => ({
      header: true, // 默认 true
      body: true, // 默认 true
      footer: true, // 默认 true
    })
  },
  mode: {
    type: String,
    default: TABLE_MODE.DEFAULT
  },
  useHorizontalVirtualScroll: {
    type: Boolean,
    default: true
  },
  popoverEffect: String,
  popoverOpenDelay: Number,
  dynamicHeightResizable: {
    type: Boolean,
    default: false
  },
  autoLowHeight: {
    type: Boolean,
    default: true
  },
  // 是否组织滚动条默认行为
  scrollPreventDefault: {
    type: Boolean,
    default: false
  },
  // 是否禁止监听滚轮行为
  stopWheel: {
    type: Boolean,
    default: false
  },
  // 用于过渡，测试环境默认支持禁用后手动设置勾选，生产环境用当前配置做开关
  checkBoxDisableSelect: {
    type: Boolean,
    default: false
  },
  // 检查收起展开是否支持定位到最后一列功能
  isCollapseMoveCheck: {
    type: Boolean,
    default: true
  },
  // 排序：点击排序的默认排序方向
  defaultClickSortOrder: {
    type: String,
    default: 'ascending'
  },
  disableHeadCheckbox: {
    type: Boolean,
    default: false
  },
}

export const coreTableProps = {
  store: Object,
  border: Boolean,
  columns: Array,
  headerHeight: Number,
  width: Number,
  height: Number,
  bodyWidth: Number,
  bodyHeight: Number,
  rowTotalHeight: Number,
  rowHeight,
  headerAlign: String,
  fixedRowsTop: Number,
  fixedRowsData: Array,
  type: String,
  tableWidth: Number,
  showIndexType: {
    type: String,
    default: 'fixd', // 这里只是注释默认是按内容，传入为fixd showIndex序号为固定有序
  },
  scroll: Function,
  emptyText: String, // 空数据时显示的文本内容
  showIndex: {
    type: Boolean,
    default: false,
  },
  showSelection: {
    type: Boolean,
    default: false,
  },
  showRadio: {
    type: Boolean,
    default: false,
  },
  radioProps: {
    type: Object,
    default: () => ({}),
  },
  showEmpty: {
    type: Boolean,
    default: false,
  },
  footerRows: Array,
  hScrollbarSize: Number,
  vScrollbarSize: Number,
  highlightCurrentRow: {
    type: Boolean,
    default: false,
  },
  enableDynamicHeight: {
    type: Boolean,
    default: false,
  },
  scrollbarStyle: Object,
  scrollbarAlwaysOn: Boolean
}



// WEBPACK FOOTER //
// ./src/public/components/kye-table2/props/table.js